import { useEffect, useRef, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Calendar } from "@/components/ui/calendar";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  CalendarDays,
  Clock,
  Mic,
  Square,
  Loader2,
  Plus,
  Trash2,
  Star,
  Repeat,
  CheckCircle2,
  Save,
  X,
} from "lucide-react";
import { createBooking, updateBooking, transcribeAudio } from "@/lib/api";
import { toast } from "sonner";

const LIST_SECTIONS = [
  { key: "todo_prior", label: "Things to do prior" },
  { key: "todo_there", label: "Things to do there" },
  { key: "to_buy", label: "Things to buy" },
  { key: "to_bring", label: "Things to bring" },
  { key: "to_discuss", label: "Things to discuss" },
];

function blankBooking(contactId, kind) {
  return {
    contact_id: contactId,
    kind: kind || "contact",
    title: "",
    scheduled_at: null,
    todo_prior: [],
    todo_there: [],
    to_buy: [],
    to_bring: [],
    to_discuss: [],
    brainstorm: "",
    journal: "",
    review: "",
    rating: 0,
    repeat: false,
    audio_clips: [],
    completed: false,
  };
}

function toDateTimeParts(iso) {
  if (!iso) return { date: undefined, time: "" };
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return { date: undefined, time: "" };
  const hh = String(d.getHours()).padStart(2, "0");
  const mm = String(d.getMinutes()).padStart(2, "0");
  return { date: d, time: `${hh}:${mm}` };
}

function combineDateTime(date, time) {
  if (!date) return null;
  const [hh, mm] = (time || "00:00").split(":").map((n) => parseInt(n, 10) || 0);
  const d = new Date(date);
  d.setHours(hh, mm, 0, 0);
  return d.toISOString();
}

function StarRating({ value, onChange }) {
  const [hover, setHover] = useState(0);
  const v = hover || value || 0;
  return (
    <div
      className="flex items-center gap-1.5"
      onMouseLeave={() => setHover(0)}
      data-testid="booking-rating"
    >
      {[1, 2, 3, 4, 5].map((n) => {
        const filled = n <= v;
        return (
          <button
            type="button"
            key={n}
            onMouseEnter={() => setHover(n)}
            onClick={() => onChange(value === n ? 0 : n)}
            className={`star-btn ${filled ? "is-filled" : ""}`}
            aria-label={`Rate ${n}`}
            data-testid={`booking-star-${n}`}
          >
            <Star
              className="h-7 w-7"
              fill={filled ? "currentColor" : "none"}
              strokeWidth={1.5}
            />
          </button>
        );
      })}
      <span className="ml-2 text-xs text-white/40 font-mono">
        {value ? `${value} / 5` : "Rate"}
      </span>
    </div>
  );
}

function ListEditor({ label, items, onChange, testidPrefix }) {
  const [draft, setDraft] = useState("");
  const add = () => {
    const s = draft.trim();
    if (!s) return;
    onChange([...(items || []), s]);
    setDraft("");
  };
  const remove = (idx) => onChange(items.filter((_, i) => i !== idx));
  return (
    <div className="space-y-2" data-testid={`list-${testidPrefix}`}>
      <div className="text-[11px] uppercase tracking-[0.18em] text-white/50 font-mono">
        {label}
      </div>
      <div className="flex gap-2">
        <Input
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              add();
            }
          }}
          placeholder="Add item & press enter"
          className="bg-black/40 border-white/10 text-white h-10"
          data-testid={`list-input-${testidPrefix}`}
        />
        <Button
          onClick={add}
          type="button"
          variant="ghost"
          className="btn-ghost h-10 px-3"
          data-testid={`list-add-${testidPrefix}`}
        >
          <Plus className="h-4 w-4" />
        </Button>
      </div>
      {items && items.length > 0 && (
        <ul className="space-y-1.5">
          {items.map((it, i) => (
            <li
              key={i}
              className="flex items-center gap-2 group bg-white/[0.03] border border-white/10 rounded-md px-3 py-2 text-sm"
            >
              <span className="text-white/80 break-words flex-1">{it}</span>
              <button
                type="button"
                onClick={() => remove(i)}
                className="opacity-60 hover:opacity-100 hover:text-red-400 transition"
                data-testid={`list-remove-${testidPrefix}-${i}`}
                aria-label="Remove item"
              >
                <Trash2 className="h-4 w-4" />
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function AudioRecorder({ onTranscribed, existing, onRemoveClip }) {
  const [recording, setRecording] = useState(false);
  const [transcribing, setTranscribing] = useState(false);
  const mediaRef = useRef(null);
  const chunksRef = useRef([]);

  const start = async () => {
    if (!navigator.mediaDevices?.getUserMedia) {
      toast.error("Microphone not supported on this device");
      return;
    }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mime = MediaRecorder.isTypeSupported("audio/webm")
        ? "audio/webm"
        : MediaRecorder.isTypeSupported("audio/mp4")
        ? "audio/mp4"
        : "";
      const mr = mime ? new MediaRecorder(stream, { mimeType: mime }) : new MediaRecorder(stream);
      chunksRef.current = [];
      mr.ondataavailable = (e) => {
        if (e.data && e.data.size > 0) chunksRef.current.push(e.data);
      };
      mr.onstop = async () => {
        stream.getTracks().forEach((t) => t.stop());
        const blob = new Blob(chunksRef.current, { type: mime || "audio/webm" });
        chunksRef.current = [];
        if (blob.size === 0) {
          setRecording(false);
          return;
        }
        setTranscribing(true);
        try {
          const ext = (mime || "audio/webm").includes("mp4") ? "m4a" : "webm";
          const res = await transcribeAudio(blob, `clip.${ext}`);
          onTranscribed({
            transcript: res.text || "",
            duration_ms: 0,
            created_at: new Date().toISOString(),
          });
          toast.success("Audio transcribed");
        } catch (e) {
          toast.error("Transcription failed");
          console.error(e);
        } finally {
          setTranscribing(false);
          setRecording(false);
        }
      };
      mediaRef.current = mr;
      mr.start();
      setRecording(true);
    } catch (e) {
      console.error(e);
      toast.error("Microphone permission denied");
    }
  };

  const stop = () => {
    if (mediaRef.current && mediaRef.current.state !== "inactive") {
      mediaRef.current.stop();
    }
  };

  return (
    <div className="space-y-3" data-testid="audio-recorder">
      <div className="flex items-center gap-3">
        {!recording ? (
          <Button
            type="button"
            onClick={start}
            disabled={transcribing}
            className="btn-neon h-10 px-4"
            data-testid="audio-record-btn"
          >
            {transcribing ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Transcribing…
              </>
            ) : (
              <>
                <Mic className="h-4 w-4 mr-2" />
                Record audio note
              </>
            )}
          </Button>
        ) : (
          <Button
            type="button"
            onClick={stop}
            className="h-10 px-4 bg-red-600 hover:bg-red-500 text-white"
            data-testid="audio-stop-btn"
          >
            <Square className="h-4 w-4 mr-2 animate-pulse" />
            Stop recording
          </Button>
        )}
        {recording && (
          <span className="text-xs font-mono text-red-400 flex items-center gap-1.5">
            <span className="recording-dot" /> Recording…
          </span>
        )}
      </div>
      {existing && existing.length > 0 && (
        <div className="space-y-2">
          {existing.map((clip, i) => (
            <div
              key={i}
              className="bg-white/[0.03] border border-white/10 rounded-md p-3 text-sm"
              data-testid={`audio-clip-${i}`}
            >
              <div className="flex items-start gap-2">
                <Mic className="h-4 w-4 mt-0.5 text-[#00e5ff] shrink-0" />
                <div className="flex-1 whitespace-pre-wrap text-white/85 break-words">
                  {clip.transcript || <span className="text-white/40">(empty transcript)</span>}
                </div>
                <button
                  type="button"
                  onClick={() => onRemoveClip(i)}
                  className="opacity-60 hover:opacity-100 hover:text-red-400 transition"
                  aria-label="Remove audio clip"
                  data-testid={`audio-clip-remove-${i}`}
                >
                  <Trash2 className="h-4 w-4" />
                </button>
              </div>
              {clip.created_at && (
                <div className="mt-1.5 text-[10px] text-white/30 font-mono">
                  {new Date(clip.created_at).toLocaleString()}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default function BookingDialog({
  open,
  onOpenChange,
  contactId,
  contactName,
  kind = "contact",
  booking, // existing booking to edit, null = create new
  onSaved,
}) {
  const [form, setForm] = useState(() => blankBooking(contactId, kind));
  const [saving, setSaving] = useState(false);
  const [calendarOpen, setCalendarOpen] = useState(false);

  useEffect(() => {
    if (open) {
      setForm(booking ? { ...blankBooking(contactId, kind), ...booking } : blankBooking(contactId, kind));
    }
  }, [open, booking, contactId, kind]);

  const { date, time } = toDateTimeParts(form.scheduled_at);

  const setField = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  const handleSave = async () => {
    setSaving(true);
    try {
      const payload = { ...form };
      if (!payload.title) payload.title = contactName ? `Meet ${contactName}` : "Booking";
      let saved;
      if (booking?.id) {
        saved = await updateBooking(booking.id, payload);
      } else {
        saved = await createBooking(payload);
      }
      toast.success(booking?.id ? "Booking updated" : "Booking saved");
      onSaved?.(saved);
      onOpenChange(false);
    } catch (e) {
      console.error(e);
      toast.error("Failed to save booking");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-3xl bg-[#0f0f0f] border border-white/10 text-white p-0 max-h-[94vh] overflow-hidden flex flex-col [&>button.absolute]:hidden"
        data-testid="booking-dialog"
      >
        <DialogHeader className="px-6 py-4 border-b border-white/10 flex flex-row items-start justify-between gap-3">
          <div className="flex-1 min-w-0">
            <DialogTitle className="font-display tracking-wide text-xl text-white">
              {booking?.id ? "Edit booking" : "New booking"}
              {contactName ? ` · ${contactName}` : ""}
            </DialogTitle>
            <DialogDescription className="text-white/40 text-xs font-mono mt-1">
              Plan, journal, rate. Audio notes auto-transcribed by Whisper.
            </DialogDescription>
          </div>
          <button
            type="button"
            onClick={() => onOpenChange(false)}
            className="text-white/50 hover:text-white transition"
            aria-label="Close"
            data-testid="booking-close-btn"
          >
            <X className="h-5 w-5" />
          </button>
        </DialogHeader>

        <div className="px-6 py-5 overflow-y-auto space-y-6 flex-1">
          {/* Title */}
          <div className="space-y-2">
            <div className="text-[11px] uppercase tracking-[0.18em] text-white/50 font-mono">
              Title
            </div>
            <Input
              value={form.title || ""}
              onChange={(e) => setField("title", e.target.value)}
              placeholder={contactName ? `Meet ${contactName}` : "Booking title"}
              className="bg-black/40 border-white/10 text-white h-11"
              data-testid="booking-title"
            />
          </div>

          {/* Date & Time */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div className="space-y-2">
              <div className="text-[11px] uppercase tracking-[0.18em] text-white/50 font-mono flex items-center gap-1.5">
                <CalendarDays className="h-3 w-3" /> Date
              </div>
              <Popover open={calendarOpen} onOpenChange={setCalendarOpen}>
                <PopoverTrigger asChild>
                  <Button
                    type="button"
                    variant="ghost"
                    className="btn-ghost h-11 w-full justify-start font-mono text-sm"
                    data-testid="booking-date-trigger"
                  >
                    <CalendarDays className="h-4 w-4 mr-2 text-[#00e5ff]" />
                    {date ? date.toLocaleDateString() : "Pick a date"}
                  </Button>
                </PopoverTrigger>
                <PopoverContent
                  className="w-auto p-0 bg-[#111] border-white/10"
                  align="start"
                >
                  <Calendar
                    mode="single"
                    selected={date}
                    onSelect={(d) => {
                      setField("scheduled_at", combineDateTime(d, time || "12:00"));
                      setCalendarOpen(false);
                    }}
                    initialFocus
                  />
                </PopoverContent>
              </Popover>
            </div>
            <div className="space-y-2">
              <div className="text-[11px] uppercase tracking-[0.18em] text-white/50 font-mono flex items-center gap-1.5">
                <Clock className="h-3 w-3" /> Time
              </div>
              <Input
                type="time"
                value={time}
                onChange={(e) =>
                  setField("scheduled_at", combineDateTime(date || new Date(), e.target.value))
                }
                className="bg-black/40 border-white/10 text-white h-11"
                data-testid="booking-time"
              />
            </div>
          </div>

          {/* Lists */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            {LIST_SECTIONS.map((sec) => (
              <ListEditor
                key={sec.key}
                label={sec.label}
                items={form[sec.key] || []}
                onChange={(arr) => setField(sec.key, arr)}
                testidPrefix={sec.key}
              />
            ))}
          </div>

          {/* Brainstorm */}
          <div className="space-y-2">
            <div className="text-[11px] uppercase tracking-[0.18em] text-white/50 font-mono">
              Brainstorm
            </div>
            <Textarea
              value={form.brainstorm || ""}
              onChange={(e) => setField("brainstorm", e.target.value)}
              placeholder="Open thoughts, ideas, what-ifs…"
              className="bg-black/40 border-white/10 text-white min-h-[90px]"
              data-testid="booking-brainstorm"
            />
          </div>

          {/* Journal blog */}
          <div className="space-y-2">
            <div className="text-[11px] uppercase tracking-[0.18em] text-white/50 font-mono">
              Journal · blog this meeting
            </div>
            <Textarea
              value={form.journal || ""}
              onChange={(e) => setField("journal", e.target.value)}
              placeholder="What happened, how it felt, who said what…"
              className="bg-black/40 border-white/10 text-white min-h-[140px]"
              data-testid="booking-journal"
            />
          </div>

          {/* Audio */}
          <div className="space-y-2">
            <div className="text-[11px] uppercase tracking-[0.18em] text-white/50 font-mono">
              Voice notes (auto-transcribed)
            </div>
            <AudioRecorder
              existing={form.audio_clips || []}
              onTranscribed={(clip) =>
                setField("audio_clips", [...(form.audio_clips || []), clip])
              }
              onRemoveClip={(i) =>
                setField(
                  "audio_clips",
                  (form.audio_clips || []).filter((_, idx) => idx !== i)
                )
              }
            />
          </div>

          {/* Review + rating + repeat */}
          <div className="space-y-2 pt-2 border-t border-white/10">
            <div className="text-[11px] uppercase tracking-[0.18em] text-white/50 font-mono">
              Review
            </div>
            <Textarea
              value={form.review || ""}
              onChange={(e) => setField("review", e.target.value)}
              placeholder="Post-meet recap, lessons, follow-ups…"
              className="bg-black/40 border-white/10 text-white min-h-[100px]"
              data-testid="booking-review"
            />
            <div className="flex flex-wrap items-center justify-between gap-4 pt-3">
              <StarRating
                value={form.rating || 0}
                onChange={(n) => setField("rating", n)}
              />
              <div className="flex items-center gap-3">
                <button
                  type="button"
                  onClick={() => setField("repeat", !form.repeat)}
                  className={`toggle-chip ${form.repeat ? "is-on" : ""}`}
                  data-testid="booking-repeat-toggle"
                >
                  <Repeat className="h-3.5 w-3.5" />
                  {form.repeat ? "Repeat · Yes" : "Repeat?"}
                </button>
                <button
                  type="button"
                  onClick={() => setField("completed", !form.completed)}
                  className={`toggle-chip ${form.completed ? "is-on" : ""}`}
                  data-testid="booking-completed-toggle"
                >
                  <CheckCircle2 className="h-3.5 w-3.5" />
                  {form.completed ? "Completed" : "Mark complete"}
                </button>
              </div>
            </div>
          </div>
        </div>

        <div className="px-6 py-4 border-t border-white/10 flex items-center justify-end gap-2">
          <Button
            type="button"
            variant="ghost"
            className="btn-ghost h-11 px-3"
            onClick={() => onOpenChange(false)}
            data-testid="booking-cancel"
          >
            Cancel
          </Button>
          <Button
            type="button"
            onClick={handleSave}
            disabled={saving}
            className="btn-neon h-11 px-5"
            data-testid="booking-save"
          >
            {saving ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" /> Saving…
              </>
            ) : (
              <>
                <Save className="h-4 w-4 mr-2" /> Save booking
              </>
            )}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
